'use client';

import { Image } from '@/components/ui/image/image';
import { Link } from '@/components/ui/link';
import { Media } from '@/components/ui/media/media';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import { cn } from '@/lib/utils';
import { buildFormContentFromLinkFields } from '@/lib/utils/form-data';
import type { SlideItem } from '../../hero-slider';
import styles from './slider-content.module.scss';

export type SliderContentProps = {
  slide: SlideItem;
  activeIndex?: number;
  totalSlides?: number;
  className?: string;
};

export const SliderContent = ({
  slide,
  activeIndex = 0,
  totalSlides = 1,
  className,
}: SliderContentProps) => {
  const ctaFields = slide.cta?.fields;
  const formContent = ctaFields ? buildFormContentFromLinkFields(ctaFields) : {};

  // Determine border radius class based on tab position
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === totalSlides - 1;
  const borderRadiusClass = isFirst
    ? styles.panel_first
    : isLast
      ? styles.panel_last
      : styles.panel_middle;

  return (
    <div key={slide.id} className={cn(styles.panel, borderRadiusClass, className)}>
      <div className={styles.content}>
        <div className={styles.text}>
          <h2 className={styles.title}>{slide.title}</h2>
          {slide.description && (
            <div className={styles.description}>
              <RichTextViewer content={slide.description} />
            </div>
          )}
        </div>
        {ctaFields && (
          <div className={styles.cta_wrapper}>
            <Link
              label={ctaFields.label}
              url={ctaFields.url}
              type={ctaFields.type}
              withArrow
              destination={ctaFields.destination}
              ctaId={ctaFields.key}
              {...formContent}
              className={styles.cta}
            />
          </div>
        )}
      </div>
      {(slide.mediaData || slide.illustration?.src) && (
        <div className={styles.illustration}>
          {slide.mediaData ? (
            <Media
              {...slide.mediaData}
              width={400}
              height={340}
              className={styles.illustration_image}
            />
          ) : slide.illustration?.src ? (
            <Image
              src={slide.illustration.src}
              alt={slide.illustration.alt || ''}
              width={400}
              height={340}
              className={styles.illustration_image}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SliderContent;
